import React, { PureComponent } from 'react';
import Autolinker from 'autolinker';
import schemify from 'url-schemify';

import { removeCustomField } from '../actions/custom-fields';

import { CUSTOM_FIELDS } from '../constants/custom-fields';
import { validateEmail } from '../utils/validators';
import { formatCurrency, formatPrice } from '../utils/helpers';
import { normalizeDateForServer } from '../utils/date';
import { getColor } from '../utils/color';
import { getLocations } from '../api/global-api';
import { createAlert } from '../services/alert-service';

import MultiLineInput from './MultiLineInput';
import DropdownElement from './dropdown-element';
import FieldDefault from './field-default';
import Checkbox from './checkbox';
import DateElement from './date-element';
import CanUser from './can-user';
import OverflowTooltip from './overflow-tooltip';
import Tooltip from './tooltip';
import DateTime from 'components/DateTime';

import IconEdit from '../icons/icon-edit';
import IconArrowDown from '../icons/icon-arrow-down';
import IconEye from '../icons/icon-eye';

const NON_EDITABLE_FIELDS = ['dropdown', 'reminder', 'checkbox', 'budget'];

const PLACEHOLDER = 'Set value...';

const ACTIONS = [
    {
        text: 'Edit Field',
        id: 'edit'
    },
    {
        text: 'Remove Field',
        id: 'remove',
        prompt: {
            title: 'DELETE FIELD',
            text: 'Do you really want to delete this field?',
            confirmText: 'Delete',
            confirmClassName: 'btn-caution'
        }
    }
];

const DropdownOption = ({ value = null, color }) => {
    const colorMatch = getColor(color);

    return <span className={colorMatch && 'label-pill'} style={colorMatch ? { width: '100%', backgroundColor: colorMatch.primary, color: colorMatch.text } : undefined}>{value}</span>
};

export default class CustomField extends PureComponent {
    static defaultProps = {
        value: '',
        trackedTime: 0,
        costPerHour: 0
    }

    state = {
        editing: !this.props.value,
        manualEdit: false
    }

    handleEditStart = () => {
        this.setState({
            editing: true,
            manualEdit: true
        }, () => {
            this.valueCtrl && this.valueCtrl.focus(true);
        });
    }

    handleChange = value => {
        value = value.trim();

        if (value !== '' && !this.validator(value)) {
            createAlert('error', `Only ${this.props.type}s are accepted in this field.`);
            this.valueCtrl && this.valueCtrl.setValue && this.valueCtrl.setValue('');

            return;
        }

        if (this.state.editing) {
            this.setState({
                editing: !value,
                manualEdit: false
            });
        }

        this.change(value);
    }

    handleLocationChange = value => {
        // this.change(value);
        // TODO: Implement location search
        // getLocations(value).then(results => {
        // });
    }

    handleDropdownChange = ({ value }) => {
        this.change(value);
    }

    handleDateChange = date => {
        this.change(normalizeDateForServer(date.toDate()));
    }


    handleActionClick = ({ id }) => {
        switch (id) {
            case 'remove':
                removeCustomField(this.props.id);
                break;

            case 'edit':
                this.props.onEdit(this.props.field);
                break;

            default:
                return null;
        }
    }

    change(value) {
        this.props.onChange && this.props.onChange(this.props.id, value);
    }

    setValueCtrl = ctrl => this.valueCtrl = ctrl;

    get canEdit() {
        return !NON_EDITABLE_FIELDS.includes(this.props.type)
    }

    get valueElement() {
        switch (this.props.type) {
            case 'dropdown':
                const selected = this.props.value && this.props.options.find(opt => opt.value === this.props.value);
                const color = selected && getColor(selected.color);
                return (
                    <DropdownElement items={this.props.options} itemComponent={<DropdownOption />} defaultXPosition="right" xAlign="left" onItemClick={this.handleDropdownChange}>
                        <span className={'action-trigger' + (!this.props.value ? ' placeholder' : '')}>
                            <span className={color && 'label-pill'} style={color ? { backgroundColor: color.primary, color: color.text } : null}>
                                {this.props.value || 'Select...'}

                                <span className="arrow">
                                    <IconArrowDown />
                                </span>
                            </span>
                        </span>
                    </DropdownElement>
                );

            case 'currency':
            case 'number':
                if (this.state.editing) {
                    return <FieldDefault value={this.props.value} replacer={this.replacer} placeholder={PLACEHOLDER} onBlur={this.handleChange} blurOnEnter={true} provideController={this.setValueCtrl} />;
                }

                if (this.props.currency) {
                    return formatCurrency(this.props.value, this.props.currency);
                }

                return this.props.value;

            case 'phone':
                if (this.state.editing) {
                    return <FieldDefault value={this.props.value} replacer={this.replacer} placeholder={PLACEHOLDER} onBlur={this.handleChange} blurOnEnter={true} provideController={this.setValueCtrl} />;
                }

                return <a target="_blank" rel="noopener noreferrer" href={`tel:${this.props.value}`}>{this.props.value}</a>;

            case 'email':
                if (this.state.editing) {
                    return <FieldDefault value={this.props.value} placeholder={PLACEHOLDER} onBlur={this.handleChange} blurOnEnter={true} provideController={this.setValueCtrl} />;
                }

                return <a target="_blank" rel="noopener noreferrer" href={`mailto:${this.props.value}`}>{this.props.value}</a>;

            case 'url':
                if (this.state.editing) {
                    return <FieldDefault value={this.props.value} placeholder={PLACEHOLDER} onBlur={this.handleChange} blurOnEnter={true} provideController={this.setValueCtrl} />;
                }

                return <a target="_blank" rel="noopener noreferrer" href={schemify(this.props.value)}>{this.props.value}</a>;

            case 'location':
                if (this.state.editing) {
                    return <FieldDefault value={this.props.value} placeholder={PLACEHOLDER} onBlur={this.handleChange} blurOnEnter={true} onChange={this.handleLocationChange} provideController={this.setValueCtrl} />;
                }

                // return <a target="_blank" rel="noopener noreferrer" href={`geo:${this.props.value}`}>{this.props.value}</a>;
                return this.props.value;

            case 'checkbox':
                return <Checkbox checked={this.props.value.toString() === 'true'} dark={true} onChange={this.handleChange} />;

            case 'reminder':
                return (
                    <DateElement
                        defaultXPosition="right"
                        xAlign="left"
                        selected={this.props.value}
                        showTimeSelect={true}
                        onChange={this.handleDateChange}
                    >
                        <span className={'action-trigger' + (!this.props.value ? ' placeholder' : '')}>
                            {
                                this.props.value ? <DateTime format="dateTime">{this.props.value}</DateTime> : 'Select date...'
                            }
                        </span>
                    </DateElement>
                );

            case 'budget': {
                if (this.props.trackingType === 'manual') {
                    return (
                        <div className="budget-calc">
                            <div className="budget-calc-main nowrap">
                                <FieldDefault type="number" value={this.props.value} placeholder="x" onBlur={this.handleChange} blurOnEnter={true} provideController={this.setValueCtrl} />

                                <span className="helper">
                                    h&nbsp;*&nbsp;${ formatPrice(this.props.costPerHour) }&nbsp;=&nbsp;
                                </span>
                            </div>

                            <span className="result">
                                ${formatPrice((this.props.value || 0) * this.props.costPerHour)}
                            </span>
                        </div>
                    );
                }

                return `$${formatPrice(this.props.trackedTime / 1000 / 60 / 60 * this.props.costPerHour)}`;
            }

            default:
                return (
                    <MultiLineInput
                        className="custom-textarea"
                        canEdit={this.state.editing}
                        value={this.props.value}
                        placeholder={PLACEHOLDER}
                        onBlur={this.handleChange}
                        provideController={this.setValueCtrl}
                    />
                );
        }
    }

    validator(value) {
        switch (this.props.type) {
            case 'email':
                return validateEmail(value);

            case 'url':
                return Autolinker.parse(value, {
                    email: false
                }).length;

            default:
                return true;

        }
    }

    replacer = value => {
        switch (this.props.type) {
            case 'currency':
            case 'number':
                return value.replace(/[^\d.,]*/gi, '');

            case 'phone':
                return value.replace(/[^\d()+\-#]*/gi, '');

            default:
                return null;
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value && this.props.value && !this.state.manualEdit) {
            this.setState({
                editing: false,
                manualEdit: false
            });
        }
    }

    render() {
        const fieldProps = CUSTOM_FIELDS.find(field => field.id === this.props.type);

        if (!fieldProps) {
            return null;
        }

        return (
            <div className={`custom-field custom-field-type-${this.props.type}` + (this.props.value === '' ? ' empty' : '')}>
                <div className="custom-field-inner">
                    {
                        !this.props.valueOnly ? (
                            <div className="custom-field-head">
                                <div className="custom-field-icon">
                                    { fieldProps.fieldIcon }
                                </div>

                                <OverflowTooltip>
                                    <div className="custom-field-name">
                                        { this.props.name }
                                    </div>
                                </OverflowTooltip>

                                {
                                    this.props.hidden.length ? (
                                        <Tooltip tooltip="This field is hidden from Guests">
                                            <div className="custom-field-meta-icon">
                                                <IconEye />
                                            </div>
                                        </Tooltip>
                                    ) : null
                                }
                            </div>
                        ) : null
                    }

                    <div className="custom-field-value">
                        { this.valueElement }
                    </div>

                    <div className="custom-field-controls">
                        {
                            this.canEdit && !this.state.editing ? (
                                <div className="custom-field-control">
                                    <button className="btn btn-square btn-xxsmall btn-simple btn-icon btn-colored-hover" onClick={this.handleEditStart}>
                                        <IconEdit />
                                    </button>
                                </div>
                            ) : null
                        }

                        {
                            this.props.onEdit ? (
                                <CanUser type="editCustomFields">
                                    <div className="custom-field-control">
                                        <DropdownElement
                                            items={ACTIONS}
                                            defaultXPosition="left"
                                            xAlign="right"
                                            onItemClick={this.handleActionClick}
                                        >
                                            <span className="actions-trigger" onClick={this.handleActionsTriggerClick}><i className="material-icons">more_horiz</i></span>
                                        </DropdownElement>
                                    </div>
                                </CanUser>
                            ) : null
                        }

                    </div>
                </div>
            </div>
        )
    }
}
